import { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { formatClubDate } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

type PaymentType = 'ALL' | 'MEMBERSHIP_NEW' | 'MEMBERSHIP_RENEWAL' | 'COMPETITION_ENTRY';
type PaymentStatus = 'ALL' | 'COMPLETED' | 'FAILED';

const PaymentRecordsList: React.FC = () => {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<PaymentType>('ALL');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fully paginate. The previous `limit: 500` with no nextToken loop
      // would silently truncate once the club passes 500 payments — on a
      // financial reconciliation screen a missing record is worse than a
      // slow load. Matches the pagination already used for PaymentRecord in
      // RegistrationDetailView.tsx and for Member.list in
      // MemberApplications.tsx.
      const all: any[] = [];
      let nextToken: string | null | undefined;
      do {
        const result: any = await (client as any).models.PaymentRecord.list({
          limit: 200,
          nextToken: nextToken || undefined,
        });

        // The Amplify data client returns GraphQL errors in `result.errors`
        // with `result.data` null — it does NOT throw, so the catch below
        // never fired for them. This screen previously did
        // `setRecords(result.data ?? [])` and ignored `errors` entirely, so
        // ANY failure (most likely an authorization error) rendered as an
        // empty table with no message at all — indistinguishable from
        // "there are no payments". Reported 15 Aug 2026 as "the payments tab
        // doesn't work". Surface it instead.
        if (result?.errors?.length) {
          console.error('PaymentRecordsList load failed', result.errors);
          setError(
            `Failed to load payment records: ${result.errors[0]?.message || 'unknown error'}`
          );
          return;
        }

        all.push(...(result?.data ?? []));
        nextToken = result?.nextToken;
      } while (nextToken);

      setRecords(all);
    } catch (e: any) {
      // Log the real error — this previously swallowed it entirely, leaving
      // no diagnostic anywhere.
      console.error('PaymentRecordsList load threw', e);
      setError(`Failed to load payment records: ${e?.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const filtered = records.filter((r) => {
    if (typeFilter !== 'ALL' && r.paymentType !== typeFilter) return false;
    if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
    if (dateFrom && r.timestamp < dateFrom) return false;
    if (dateTo && r.timestamp > dateTo + 'T23:59:59Z') return false;
    return true;
  });

  if (loading) return <div className="text-gray-500 py-8 text-center">Loading payment records…</div>;
  if (error)
    return (
      <div className="py-4">
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 mb-3">
          {error}
        </div>
        <button
          onClick={load}
          className="px-4 py-2 bg-fishing-blue text-white rounded-md text-sm font-semibold hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-gray-50 p-4 rounded-lg">
        <div>
          <label className="block text-xs text-gray-600 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Type</label>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as PaymentType)}
            className="border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="ALL">All types</option>
            <option value="MEMBERSHIP_NEW">New membership</option>
            <option value="MEMBERSHIP_RENEWAL">Renewal</option>
            <option value="COMPETITION_ENTRY">Competition entry</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as PaymentStatus)}
            className="border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="ALL">All statuses</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>
        <div className="text-sm text-gray-500 self-end pb-1">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Date', 'Payer', 'Type', 'Amount', 'Status', 'Square ID'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No records found.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatClubDate(r.timestamp)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{r.payerName}</div>
                  <div className="text-gray-500 text-xs">{r.payerEmail}</div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                  {r.paymentType === 'MEMBERSHIP_NEW' ? 'New membership'
                    : r.paymentType === 'MEMBERSHIP_RENEWAL' ? 'Renewal'
                    : 'Comp entry'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap font-medium">${((r.amountCents ?? 0) / 100).toFixed(2)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 font-mono">{r.squarePaymentId}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaymentRecordsList;
